import { Group, Loader, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleX,
  IconInfoCircle,
  IconQuestionMark,
  type TablerIcon
} from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  MANTINE_CELL_ICON_SIZE,
  MantineCellIcon
} from "../../../../../mantine/mrt/components/cell/MantineCellIcon/MantineCellIcon";
import { THEME_VARS } from "../../../../../utils/constants";
import { IconHelpText } from "../../../../IconHelpText/IconHelpText";
import type { EnergyDataImportRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import {
  getStatusToRepresentation,
  EnergyDataFileImportRequestStatus
} from "../../../EnergyDataUploadFlow/EnergyDataUpload";

interface Props {
  file: EnergyDataImportRequest;
}

function StatusCell({ file }: Props) {
  const { t } = useTranslation();
  const { errorMessage } = file;
  const { icon, color, label } = getStatusDisplay(file, t);
  const showTooltip =
    !!errorMessage &&
    (file.status === EnergyDataFileImportRequestStatus.Error ||
      file.status === EnergyDataFileImportRequestStatus.Stopped);

  const labelText = (
    <Text component="span" inherit>
      {label}
    </Text>
  );

  if (showTooltip) {
    return (
      <IconHelpText
        color={color}
        helpText={errorMessage}
        Icon={icon}
        size={MANTINE_CELL_ICON_SIZE}
        tooltipMaxWidth={320}
      >
        {labelText}
      </IconHelpText>
    );
  }

  return (
    <Group align="center" gap="xs" wrap="nowrap">
      {file.status === EnergyDataFileImportRequestStatus.InProgress ? (
        <Loader size="xs" />
      ) : (
        <MantineCellIcon iconColor={color} IconComponent={icon} />
      )}
      {labelText}
    </Group>
  );
}

function getStatusDisplay(
  file: EnergyDataImportRequest,
  t: TFunction
): {
  icon: TablerIcon;
  color: string;
  label: string;
} {
  switch (file.status) {
    case EnergyDataFileImportRequestStatus.Error:
      return isUnknownError(file, t)
        ? {
            icon: IconInfoCircle,
            color: THEME_VARS.error[6],
            label: t("errors.UnknownError")
          }
        : {
            icon: IconAlertTriangle,
            color: THEME_VARS.error[6],
            label: t("errors.EnergyDataUpload.UploadFailed")
          };
    case EnergyDataFileImportRequestStatus.Success:
      return {
        icon: IconCheck,
        color: THEME_VARS.success[6],
        label: getStatusToRepresentation(file.status)
      };
    case EnergyDataFileImportRequestStatus.Stopped:
      return {
        icon: IconCircleX,
        color: THEME_VARS.warning[6],
        label: getStatusToRepresentation(file.status)
      };
    default:
      return {
        icon: IconQuestionMark,
        color: THEME_VARS.neutral[6],
        label: getStatusToRepresentation(file.status)
      };
  }
}

function isUnknownError(file: EnergyDataImportRequest, t: TFunction): boolean {
  return !file.errorMessage || file.errorMessage === t("errors.UnknownError");
}

export { StatusCell };
